import {
  BACKLOG_CRITICAL_RATIO,
  DecodeBacklogDwell,
  DEFAULT_CONGESTION_THRESHOLDS,
  DECODE_LAG_DWELL_MS,
  PlayoutTrendGate,
  backlogCriticalRatio,
  decisiveCongestionSignal,
  evaluateCongestionSignal,
  withoutBacklogSignals,
  withoutStandingQueue,
  classifyLinkImpairment,
  WirelessLossBurst,
} from '@playcastdotio/PeerCore';
import type {
  ClientStreamStatsMessage,
  CongestionSignalAvailability,
  CongestionVerdict,
  StreamCongestionInputs,
  WirelessLossPhase,
} from '@playcastdotio/PeerCore';
import type { RecoveryClimbManager } from './RecoveryClimbManager';
import type { DeepeningBlock, RecoveryEntryManager } from './RecoveryEntryManager';

/**
 * Which side measured a sample. Only the guest can see the decode side, so only guest samples
 * may feed (or clear) the decode-backlog dwells; a host poll must leave them untouched.
 * Standing-queue dwell is fed from BOTH sides — host RTT blips are the Safari hard-cut shape.
 */
export type StatsSource = 'guest' | 'host';

export type RecoveryVerdictRouterDeps = {
  entry: RecoveryEntryManager;
  climb: RecoveryClimbManager;
  isNormal: (peerId: string) => boolean;
  deepeningBlock: (peerId: string, bypassSettle: boolean) => DeepeningBlock | null;
  getSignalAvailability?: (peerId: string) => CongestionSignalAvailability | undefined;
  /**
   * One call when a wireless-loss burst ends. `framesDropped` is the guest's dropped-frame
   * count across that burst, so an engine that pays for an IDR can skip a burst that
   * retransmission already healed.
   */
  onWirelessLossEnded?: (peerId: string, framesDropped: number) => void;
  debug: (tag: string, line: Record<string, unknown>) => void;
  nowMs: () => number;
};

/** A guest message normalised for the ladder, with the trend the dwell gate withheld (if any). */
export type GuestLadderSample = {
  inputs: StreamCongestionInputs;
  verdict: CongestionVerdict;
  heldTrendMs?: number;
};

/**
 * RecoveryVerdictRouter — one ladder verdict, one routed action.
 *
 * The single entry point both stat sources funnel through, so a guest measurement and a host
 * measurement carrying identical inputs drive identical state. Owns what a verdict needs
 * before it may be acted on: the playout-trend dwell gate (a single tick is not a trend — see
 * PlayoutTrendGate), the two decode-backlog dwells (a constant backlog escalates on its own
 * clock; a backlog-only critical must dwell before it hard-cuts), and the standing-queue dwell
 * (Safari single-sample RTT blips must not hard-cut). It then hands the sample to the entry
 * manager (cuts) or the climb manager (exit ramp).
 */
export class RecoveryVerdictRouter {
  private readonly trendGate: PlayoutTrendGate;
  /** Per-peer decode-backlog dwell for the sustained (constant-backlog) trigger. */
  private readonly sustainedBacklogDwell = new Map<string, DecodeBacklogDwell>();
  /**
   * Per-peer dwell for the deduped backlog signal, fed the unitless `backlogCriticalRatio` so
   * a backlog reported in ms one tick and in frames the next shares one clock.
   */
  private readonly criticalBacklogDwell = new Map<string, DecodeBacklogDwell>();
  /**
   * Per-peer dwell for standing-queue-only criticals. Fed from host and guest samples so a
   * one-tick host RTT spike (Safari live shape: 7→201 ms) soft-cuts only until it holds.
   */
  private readonly standingQueueDwell = new Map<string, DecodeBacklogDwell>();
  private readonly wirelessLoss = new Map<string, WirelessLossBurst>();

  constructor(private readonly deps: RecoveryVerdictRouterDeps) {
    this.trendGate = new PlayoutTrendGate({ nowMs: deps.nowMs });
  }

  /** Normalise a guest message into ladder inputs; the playout trend passes through its dwell gate. */
  public guestSample(peerId: string, msg: ClientStreamStatsMessage, deliveredFps: number): GuestLadderSample {
    const availability = this.deps.getSignalAvailability?.(peerId);
    const gatedTrend = this.trendGate.gate(peerId, msg.playoutLatencyTrendMs, DEFAULT_CONGESTION_THRESHOLDS.playoutTrendElevatedMs);
    const inputs: StreamCongestionInputs = {
      standingQueueMs: msg.standingQueueMs ?? null,
      packetsLostPct: msg.packetsLostPct,
      playoutLatencyTrendMs: gatedTrend,
      decodeBacklogFrames: msg.decodeBacklogFrames,
      freezeDelta: availability?.freeze === false
        ? undefined
        : (msg.receiverCounters?.freezes ?? msg.freezeDelta),
      // Only the guest can see its decoder: on Safari a starving decoder collapses to a few
      // fps while RTT and queueing stay flat, so decodeFps is the only signal that moves.
      decodeFps: msg.decodeFps,
      deliveredFps,
      decodeMsPerFrame: msg.decodeMsPerFrame,
      availability,
    };
    const held = gatedTrend === undefined && msg.playoutLatencyTrendMs !== undefined;
    return { inputs, verdict: evaluateCongestionSignal(inputs), ...(held ? { heldTrendMs: msg.playoutLatencyTrendMs } : {}) };
  }

  /** Route one sample. `source` isolates loss bursts and decides whether it may touch the decode-backlog dwells. */
  public ingest(
    peerId: string,
    rawInputs: StreamCongestionInputs,
    source: StatsSource,
    heldTrendMs?: number,
    framesDropped?: number,
    rttMs?: number,
  ): void {
    const wirelessKey = `${source}:${peerId}`;
    const wirelessPhase = this.noteWirelessLoss(wirelessKey, rawInputs, framesDropped, rttMs);
    if (wirelessPhase === 'ended') {
      const dropped = this.wirelessLoss.get(wirelessKey)?.takeDroppedFrames() ?? 0;
      this.deps.onWirelessLossEnded?.(peerId, dropped);
    }
    // Ignore the closing retransmit's queue, while preserving independent decoder
    // and freeze evidence. Every guest sample must still feed the backlog dwells.
    const inputs = wirelessPhase === 'ended' ? withoutStandingQueue(rawInputs) : rawInputs;
    const verdict = evaluateCongestionSignal(inputs);
    if (verdict === 'critical' || verdict === 'elevated') this.deps.climb.invalidateTransportEvidence(peerId);
    // Loss and freeze criticals are instantaneous, network-proven signals: they never wait
    // for the connect-settle gate (the explicit exception ConnectSettleGate documents).
    const bypassSettle =
      (inputs.packetsLostPct ?? 0) >= DEFAULT_CONGESTION_THRESHOLDS.lossCriticalPct ||
      (inputs.availability?.freeze !== false &&
        (inputs.freezeDelta ?? 0) >= DEFAULT_CONGESTION_THRESHOLDS.freezeCriticalDelta);
    const decisiveSignal = decisiveCongestionSignal(inputs, verdict);
    const block = this.deps.deepeningBlock(peerId, bypassSettle);
    // Standing-queue dwell is fed every tick (host + guest) so a blip that clears resets the
    // clock. Settle / standing-buffer blocks must not accumulate: wall-clock advances during
    // CONNECT_SETTLE_MS would otherwise make the first unblocked tick an instant hard cut.
    const standingQueueState = this.standingQueueDwellFor(peerId).observe(
      block != null
        ? undefined
        : typeof inputs.standingQueueMs === 'number' && Number.isFinite(inputs.standingQueueMs)
          ? inputs.standingQueueMs
          : undefined,
    );
    this.deps.debug('[Recovery.ingest]', {
      peerId,
      verdict,
      standingQueueMs: inputs.standingQueueMs ?? 'absent',
      packetsLostPct: inputs.packetsLostPct ?? 'absent',
      decodeFps: inputs.decodeFps ?? 'absent',
      deliveredFps: inputs.deliveredFps ?? 'absent',
      decodeMsPerFrame: inputs.decodeMsPerFrame ?? 'absent',
      decodeBacklogFrames: inputs.decodeBacklogFrames ?? 'absent',
      playoutLatencyTrendMs: inputs.playoutLatencyTrendMs ?? 'absent',
      freezeDelta: inputs.freezeDelta ?? 'absent',
      which: decisiveSignal,
      standingQueueDwell: standingQueueState,
      ...(heldTrendMs !== undefined ? { trendHeldMs: heldTrendMs } : {}),
      ...(block === 'settling' ? { settling: true } : {}),
      ...(block === 'standing-buffer' ? { suppressed: 'standing-buffer' } : {}),
    });

    // Both dwells are fed from EVERY guest sample, including the ones that read clear — a
    // dwell that stops being observed keeps a stale start time. Never from a host poll: the
    // host cannot measure the decode side, and its absent backlog would reset both dwells.
    const sustainedBacklog =
      source === 'guest' ? this.sustainedDwellFor(peerId).observe(inputs.decodeBacklogFrames) : 'clear';
    const criticalBacklog =
      source === 'guest' ? this.criticalDwellFor(peerId).observe(backlogCriticalRatio(inputs)) : 'clear';

    // A constant decode backlog (Android's steady decoder lag) escalates on its own clock;
    // growth-based signals cannot see a backlog that never shrinks.
    if (verdict !== 'critical' && sustainedBacklog === 'trip') {
      this.deps.entry.enterOrEscalate(peerId, 'sustained-decode-backlog');
      return;
    }
    // A critical resting on the deduped backlog signal ALONE must dwell before it hard-cuts.
    // A critical resting on standing queue ALONE must also dwell (Safari RTT blips).
    // Loss, freeze, and decodeFps stay instant.
    if (verdict === 'critical') {
      if (this.lossIsWireless(inputs, decisiveSignal, rttMs)) return;
      const withoutBacklog = withoutBacklogSignals(inputs);
      const criticalWithoutBacklog = evaluateCongestionSignal(withoutBacklog) === 'critical';
      if (!criticalWithoutBacklog && criticalBacklog !== 'trip') {
        this.deps.entry.enterElevated(peerId);
        return;
      }
      if (criticalWithoutBacklog) {
        const criticalWithoutQueue =
          evaluateCongestionSignal(withoutStandingQueue(withoutBacklog)) === 'critical';
        if (!criticalWithoutQueue && standingQueueState !== 'trip') {
          this.deps.entry.enterElevated(peerId);
          return;
        }
      }
      this.deps.entry.enterOrEscalate(peerId, 'congestion-critical', bypassSettle);
      return;
    }
    // An untrusted floor is not evidence of health: it may neither advance an exit nor be
    // read as a queue. A distance-independent signal can still have raised `elevated`.
    const standingQueueMs = inputs.standingQueueMs;
    if (standingQueueMs == null || !Number.isFinite(standingQueueMs)) {
      if (source === 'host') this.deps.climb.invalidateTransportEvidence(peerId);
      if (verdict === 'elevated') this.deps.entry.enterElevated(peerId);
      return;
    }
    if (this.deps.entry.considerSustainedQueueEscalation(peerId, standingQueueMs)) return;
    // Elevated only *enters* an episode. For a peer already recovering it falls through to
    // the exit gate, which clears `healthySince` itself when the sample is not healthy.
    if (verdict === 'elevated' && this.deps.isNormal(peerId)) {
      if (this.lossIsWireless(inputs, decisiveSignal, rttMs)) return;
      this.deps.entry.enterElevated(peerId);
      return;
    }
    this.deps.climb.considerExit(peerId, standingQueueMs);
  }

  public clearPeer(peerId: string): void {
    this.trendGate.clearPeer(peerId);
    this.sustainedBacklogDwell.delete(peerId);
    this.criticalBacklogDwell.delete(peerId);
    this.standingQueueDwell.delete(peerId);
    this.wirelessLoss.delete(`host:${peerId}`);
    this.wirelessLoss.delete(`guest:${peerId}`);
  }

  public clearAll(): void {
    this.trendGate.clearAll();
    this.sustainedBacklogDwell.clear();
    this.criticalBacklogDwell.clear();
    this.standingQueueDwell.clear();
    this.wirelessLoss.clear();
  }

  private sustainedDwellFor(peerId: string): DecodeBacklogDwell {
    let dwell = this.sustainedBacklogDwell.get(peerId);
    if (!dwell) {
      dwell = new DecodeBacklogDwell({
        thresholdFrames: DEFAULT_CONGESTION_THRESHOLDS.decodeBacklogSustainedFrames,
        holdMs: DEFAULT_CONGESTION_THRESHOLDS.decodeBacklogSustainedMs,
        nowMs: this.deps.nowMs,
      });
      this.sustainedBacklogDwell.set(peerId, dwell);
    }
    return dwell;
  }

  /** Fed the unitless backlogCriticalRatio: ≥ 1 is critical whichever unit the guest reported. */
  private criticalDwellFor(peerId: string): DecodeBacklogDwell {
    let dwell = this.criticalBacklogDwell.get(peerId);
    if (!dwell) {
      dwell = new DecodeBacklogDwell({ thresholdFrames: BACKLOG_CRITICAL_RATIO, holdMs: DECODE_LAG_DWELL_MS, nowMs: this.deps.nowMs });
      this.criticalBacklogDwell.set(peerId, dwell);
    }
    return dwell;
  }

  /** Fed standingQueueMs; trips once the queue has held at/above queueEnterMs for DECODE_LAG_DWELL_MS. */
  private standingQueueDwellFor(peerId: string): DecodeBacklogDwell {
    let dwell = this.standingQueueDwell.get(peerId);
    if (!dwell) {
      dwell = new DecodeBacklogDwell({
        thresholdFrames: DEFAULT_CONGESTION_THRESHOLDS.queueEnterMs,
        holdMs: DECODE_LAG_DWELL_MS,
        nowMs: this.deps.nowMs,
      });
      this.standingQueueDwell.set(peerId, dwell);
    }
    return dwell;
  }

  /**
   * Flat-round-trip loss is not a capacity failure. The burst tracker still runs so the
   * end of the burst can request one keyframe.
   */
  private noteWirelessLoss(
    key: string,
    inputs: StreamCongestionInputs,
    framesDropped?: number,
    rttMs?: number,
  ): WirelessLossPhase {
    let burst = this.wirelessLoss.get(key);
    if (!burst) {
      burst = new WirelessLossBurst();
      this.wirelessLoss.set(key, burst);
    }
    return burst.observe(classifyLinkImpairment(this.impairmentSample(inputs, rttMs)), framesDropped ?? 0);
  }

  private lossIsWireless(inputs: StreamCongestionInputs, signal: string, rttMs?: number): boolean {
    return signal === 'loss' && classifyLinkImpairment(this.impairmentSample(inputs, rttMs)) === 'wireless-loss';
  }

  /** Absolute RTT is passed only while the standing queue is absent. A measured queue wins. */
  private impairmentSample(inputs: StreamCongestionInputs, rttMs?: number) {
    const queue = inputs.standingQueueMs;
    return {
      packetsLostPct: inputs.packetsLostPct,
      standingQueueMs: queue,
      ...(typeof queue === 'number' ? {} : { rttMs }),
    };
  }
}
